import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Skeleton,
  Text,
  Wrap,
  useColorModeValue as mode,
  Tooltip,
} from "@chakra-ui/react";
import { addDays } from "date-fns";
import { useMemo, useState } from "react";
import { BiCommentDetail } from "react-icons/bi";
import { TbBrandBooking } from "react-icons/tb";
import { AiTwotoneCopyright } from "react-icons/ai";
import { BsRobot } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { compareDatesOnly, formatAUDate, formatAUTime } from "../global-client";
import PostcodeModal from "../screens/screenComponents/PostcodeModal";
import CustomerHistoryModal from "./CustomerHistoryModal";

const JobCard = ({ job, loading, listType, selectionMode, isSelected, onSelectionChange, onJobCardClick }) => {
  const [postcodeModalOpen, setPostcodeModalOpen] = useState(false);
  const [customerHistoryModalOpen, setCustomerHistoryModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleJobCardClick = () => {
    // Don't navigate if in selection mode
    if (selectionMode) return;
    
    // Save current listType to localStorage before navigating
    if (["1", "2", "3", "4"].includes(listType)) {
      localStorage.setItem("jobsListType", listType);
    }

    if (onJobCardClick) {
      onJobCardClick(job)
    }

    navigate(`/job/${job.JOB_NO}`);
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (onSelectionChange) {
      onSelectionChange(job.JOB_ID);
    }
  };

  const today = new Date();
  const tomorrow = addDays(new Date(), 1);
  const jobETA = new Date(job.ETA);

  const etaTone =
    compareDatesOnly(jobETA, today) <= 0 ? "overdue" : compareDatesOnly(jobETA, tomorrow) === 0 ? "soon" : "upcoming";
  const etaColor = etaTone === "overdue" ? "red" : etaTone === "soon" ? "yellow" : "blue";
  const statusTone =
    job.PROGRESS_STATUS === "Started"
      ? "green"
      : job.PROGRESS_STATUS === "Paused"
      ? "blue"
      : job.PROGRESS_STATUS === "Finished"
      ? "gray"
      : "orange";
  const displayJobName = job.JOB_NAME2 && job.JOB_NAME2 !== "" ? job.JOB_NAME2 : job.JOB_NAME1;

  const etaLabel = useMemo(() => formatAUDate(job.ETA, { weekday: true }) + " • " + formatAUTime(job.ETA), [job.ETA]);

  const iconColor = mode("#3182CE", "#63B3ED")
  const hoverColor = mode("blue.600", "blue.300")

  return (
    <Skeleton isLoaded={!loading} onClick={handleJobCardClick} h={{ base: "auto", md: "100%" }} display='flex'>
      <Box
        h={{ base: "auto", md: "100%" }}
        display='flex'
        flexDirection='column'
        bg={mode("white", "gray.800")}
        bgGradient={mode(`linear(to-r, ${etaColor}.50, white)`, `linear(to-r, ${etaColor}.900, gray.800)`)}
        borderWidth='1px'
        borderColor={mode(`${etaColor}.200`, `${etaColor}.600`)}
        borderRadius='14px'
        px='3'
        py='3'
        shadow='md'
        transition='all 0.25s ease'
        _hover={{ shadow: "lg", transform: "translateY(-2px)" }}
        _active={{ transform: "translateY(-1px)" }}
        w='95%'
        mb='0'
        ml='3'
        cursor={selectionMode ? "default" : "pointer"}
        position='relative'
      >
        {selectionMode && (
          <Checkbox
            position='absolute'
            top='2'
            left='2'
            isChecked={isSelected}
            onChange={handleCheckboxClick}
            size='lg'
            colorScheme='blue'
            zIndex={1}
          />
        )}
        <Flex align='flex-start' justify='space-between' gap='3' ml={selectionMode ? '8' : '0'}>
          <Flex align='center' gap='1' minW='0'>
            {job.BOOKING === 1 && <TbBrandBooking size='20' color={iconColor} />}
            {job.BOOKING === 1 && job.CONFIRMED === 1 && <AiTwotoneCopyright size='20' color={iconColor} />}
            <Badge colorScheme={etaColor} variant='solid' fontSize='sm' px='2' borderRadius='md' ml='0' >
              {job.JOB_NO}
            </Badge>
            <Text fontWeight='semibold' noOfLines={1} color={mode("gray.800", "gray.100")}>
              {job.CONSOLE_NAME}
            </Text>
          </Flex>
          <Badge
            colorScheme={etaColor}
            variant='subtle'
            fontSize='xs'
            px='2'
            borderRadius='full'
            textDecoration={job.IRREPARABLE === 1 ? "line-through" : "none"}
          >
            {etaLabel}
          </Badge>
        </Flex>

        <Flex align='center' justify='space-between' gap='3' mt='2'>
          <Box minW='0'>
            <Text noOfLines={1} fontWeight='bold' fontSize='md'>
              {displayJobName}
            </Text>
            <Wrap spacing='1' align='center' color={mode("gray.600", "gray.300")}>
              {listType === "history" ? (
                <Text noOfLines={1} fontSize='sm'>
                  {job.CUST_FNAME + " " + job.CUST_SNAME}
                </Text>
              ) : (
                <Text
                  noOfLines={1}
                  fontSize='sm'
                  cursor='pointer'
                  textDecoration='underline'
                  textUnderlineOffset='2px'
                  onClick={(event) => {
                    event.stopPropagation();
                    setCustomerHistoryModalOpen(true);
                  }}
                  _hover={{ color: hoverColor }}
                >
                  {job.CUST_FNAME + " " + job.CUST_SNAME}
                </Text>
              )}
              <Text
                fontSize='sm'
                fontWeight='semibold'
                textDecoration='underline'
                textUnderlineOffset='3px'
                onClick={(event) => {
                  event.stopPropagation();
                  setPostcodeModalOpen(true);
                }}
                cursor='pointer'
                ml='1'
              >
                ({job.CUST_POSTCODE})
              </Text>
            </Wrap>
          </Box>
          <Text fontWeight='extrabold' fontSize='lg' color={mode("green.600", "green.200")}>
            ${job.TOTAL}
          </Text>
        </Flex>

        <Flex align='center' justify='space-between' gap='3' mt='3' minH='36px'>
          <HStack spacing='2'>
            {(job.PROGRESS_STATUS === "Started" ||
              job.PROGRESS_STATUS === "Paused" ||
              job.PROGRESS_STATUS === "Finished") && (
              <Badge colorScheme={statusTone} variant='solid' fontSize='xs' px='2' borderRadius='full'>
                {job.PROGRESS_STATUS === "Started" ? "In-Progress" : job.PROGRESS_STATUS}
              </Badge>
            )}
            {job.PICKEDUP === 1 && (
              <Badge colorScheme='purple' variant='solid' fontSize='xs' px='2' borderRadius='full'>
                Picked-Up
              </Badge>
            )}
            {job.SMS_SCHEDULED && (
              <Tooltip 
                label={job.SMS_SCHEDULE_INFO && job.SMS_SCHEDULE_INFO[0] 
                  ? `SMS scheduled for ${formatAUDate(job.SMS_SCHEDULE_INFO[0].scheduleDateTime, { weekday: false })} ${formatAUTime(job.SMS_SCHEDULE_INFO[0].scheduleDateTime)}` 
                  : "SMS scheduled"
                }
                fontSize='xs'
                placement='top'
              >
                <Badge colorScheme='blue' variant='solid' fontSize='xs' px='2' borderRadius='full' display='flex' alignItems='center' gap='1'>
                  <BsRobot size='12' />
                  SMS Scheduled
                </Badge>
              </Tooltip>
            )}
          </HStack>
          <HStack spacing='1'>
            <Button
              size='xs'
              colorScheme='blue'
              variant='ghost'
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/jobs/edit/${job.JOB_NO}`);
              }}
            >
              Edit
            </Button>
            {(job.JOB_COMMENTS || job.HID_COMMENTS) && (
              <Popover trigger='hover' placement='left' isLazy closeOnBlur>
                <PopoverTrigger>
                  <IconButton
                    icon={<BiCommentDetail size='16' />}
                    colorScheme='gray'
                    variant='ghost'
                    aria-label='View job comments'
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverHeader>Job #{job.JOB_NO}</PopoverHeader>
                    <PopoverCloseButton />
                    <PopoverBody>{job.JOB_COMMENTS}</PopoverBody>
                    <PopoverFooter>{job.HID_COMMENTS}</PopoverFooter>
                  </PopoverContent>
                </Portal>
              </Popover>
            )}
          </HStack>
        </Flex>
        <PostcodeModal
          isOpen={postcodeModalOpen}
          onClose={() => setPostcodeModalOpen(false)}
          postcode={job.CUST_POSTCODE}
        />
        {listType !== "history" && (
          <CustomerHistoryModal
            isOpen={customerHistoryModalOpen}
            onClose={() => setCustomerHistoryModalOpen(false)}
            customerName={job.CUST_FNAME + " " + job.CUST_SNAME}
            customerId={job.CUSTOMER_ID}
          />
        )}
      </Box>
    </Skeleton>
  );
};

export default JobCard;
